import {
  getBaseHrefCookie,
  isEntidadeIdNumerico
} from './EntidadeStorageUtils';

export type BaseHrefEntidade = {
  idEntidade: string;
  mes: number;
  ano: number;
};

// Mesmo formato usado pelo BaseHref de @elotech/angular-common: e{id}m{mes}a{ano}
const BASE_HREF_PATTERN = /e(\d+)m(\d+)a(\d+)/;
const ENTIDADE_NAO_RESOLVIDA = '0';

export const extractEntidadeFromPath = (
  pathname: string = window.location.pathname
): BaseHrefEntidade | null => {
  const match = pathname.match(BASE_HREF_PATTERN);

  if (!match) {
    return null;
  }

  return {
    idEntidade: match[1],
    mes: Number(match[2]),
    ano: Number(match[3])
  };
};

export const buildEntidadeSegment = ({
  idEntidade,
  mes,
  ano
}: BaseHrefEntidade): string => `e${idEntidade}m${mes}a${ano}`;

export const buildRouterBasename = (): string => {
  const entidade = extractEntidadeFromPath();

  return entidade
    ? `${process.env.PUBLIC_URL}/${buildEntidadeSegment(entidade)}`
    : process.env.PUBLIC_URL || '';
};

const replaceSegmentInPathname = (novoSegmento: string): string => {
  const pathname = window.location.pathname;
  const match = pathname.match(BASE_HREF_PATTERN);

  if (match && typeof match.index === 'number') {
    return `${pathname.slice(0, match.index)}${novoSegmento}${pathname.slice(
      match.index + match[0].length
    )}`;
  }

  const publicUrl = process.env.PUBLIC_URL || '';
  const relativePath = pathname.startsWith(publicUrl)
    ? pathname.slice(publicUrl.length)
    : pathname;
  const restante = relativePath === '/' ? '' : relativePath;

  return `${publicUrl}/${novoSegmento}${restante}`;
};

export const isEntidadeNaoResolvida = (idEntidade: string): boolean =>
  idEntidade === ENTIDADE_NAO_RESOLVIDA;

export const resolveBaseHref = (): BaseHrefEntidade => {
  const existente = extractEntidadeFromPath();

  if (existente) {
    return existente;
  }

  const cookie = getBaseHrefCookie();
  const agora = new Date();
  // O cookie BASE_HREF é escrito por todo o host (inclusive pelo lado Angular,
  // que também trabalha com ids em UUID), então nunca confiamos no seu conteúdo:
  // um valor não numérico geraria um segmento que o BASE_HREF_PATTERN não
  // consegue reparsear, quebrando todas as leituras posteriores da URL.
  const entidadeCookie = cookie?.entidade;
  const mesCookie = cookie?.mes;
  const anoCookie = cookie?.ano;
  const resolvido: BaseHrefEntidade = isEntidadeIdNumerico(entidadeCookie)
    ? {
        idEntidade: entidadeCookie.toString(),
        mes: isEntidadeIdNumerico(mesCookie)
          ? Number(mesCookie)
          : agora.getMonth() + 1,
        ano: isEntidadeIdNumerico(anoCookie)
          ? Number(anoCookie)
          : agora.getFullYear()
      }
    : {
        idEntidade: ENTIDADE_NAO_RESOLVIDA,
        mes: agora.getMonth() + 1,
        ano: agora.getFullYear()
      };

  const novoPathname = replaceSegmentInPathname(
    buildEntidadeSegment(resolvido)
  );
  window.history.replaceState(
    window.history.state,
    '',
    `${novoPathname}${window.location.search}${window.location.hash}`
  );

  return resolvido;
};

export const changeEntidade = (idEntidade: string | number): void => {
  const atual = extractEntidadeFromPath();

  if (!atual) {
    throw new Error(
      'Não é possível trocar de entidade sem um segmento de entidade já resolvido na URL.'
    );
  }

  const novoSegmento = buildEntidadeSegment({
    idEntidade: String(idEntidade),
    mes: atual.mes,
    ano: atual.ano
  });
  const novoPathname = replaceSegmentInPathname(novoSegmento);

  window.location.assign(
    `${novoPathname}${window.location.search}${window.location.hash}`
  );
};

// Como changeEntidade, mas também funciona quando a URL ainda não tem
// nenhum segmento de entidade (injeta um novo em vez de lançar) — caso do
// bootstrap anônimo que descobre, após montar, que o usuário já está
// autenticado.
export const assignEntidadeSegment = (idEntidade: string | number): void => {
  const atual = extractEntidadeFromPath();
  const agora = new Date();
  const novoSegmento = buildEntidadeSegment({
    idEntidade: String(idEntidade),
    mes: atual?.mes ?? agora.getMonth() + 1,
    ano: atual?.ano ?? agora.getFullYear()
  });
  const novoPathname = replaceSegmentInPathname(novoSegmento);

  window.location.assign(
    `${novoPathname}${window.location.search}${window.location.hash}`
  );
};
